import { useTranslation } from '../../../i18n/useTranslation';
import { Badge } from '../../atoms/Badge/Badge';
import { Button } from '../../atoms/Button/Button';
import { Card } from '../../atoms/Card/Card';
import { LiveRegion } from '../../atoms/LiveRegion/LiveRegion';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import { LoadErrorState } from '../../feedback/LoadErrorState/LoadErrorState';
import { Section } from '../../templates/Section/Section';
import { useOverviewAnalyses } from '../../../hooks/useLivestockOverview';
import type { OverviewAnalysisSummary } from '../../../services/livestockOverviewService';
import { formatOverviewDate } from './overviewText';
import './LivestockOverview.css';

export interface OverviewPreviousAnalysesProps {
  aquariumId: string;
  onOpen: (analysisId: string) => void;
}

const STATUS_TONE: Record<OverviewAnalysisSummary['status'], 'info' | 'success' | 'danger'> = {
  detecting: 'info',
  identifying: 'info',
  ready: 'success',
  failed: 'danger',
};

/**
 * "Continue a previous analysis": the aquarium's recent overview analyses (kept 30 days).
 * Renders nothing while there are none — the upload above is the page's main content.
 */
export function OverviewPreviousAnalyses({ aquariumId, onOpen }: OverviewPreviousAnalysesProps) {
  const { t, i18n } = useTranslation();
  const { data, isLoading, isError, refetch } = useOverviewAnalyses(aquariumId);

  if (isLoading) {
    return (
      <LiveRegion busy>
        <Text color="muted">{t('livestockOverview.previous.loading')}</Text>
      </LiveRegion>
    );
  }

  if (isError) {
    return (
      <LoadErrorState
        compact
        title={t('livestockOverview.previous.loadError')}
        titleLevel={2}
        onRetry={() => void refetch()}
        retryLabel={t('livestockOverview.previous.retry')}
      />
    );
  }

  const analyses = data ?? [];
  if (analyses.length === 0) return null;

  return (
    <Section title={t('livestockOverview.previous.title')}>
      <ul className="livestock-overview__previous-list">
        {analyses.map((analysis) => {
          const date = formatOverviewDate(analysis.createdAt, i18n.language);
          return (
            <li key={analysis.id}>
              <Card padding="md">
                <div className="livestock-overview__previous-item">
                  <Stack gap="1">
                    <Text weight="semibold">{date}</Text>
                    <Text size="sm" color="muted">
                      {t('livestockOverview.previous.summary', {
                        found: analysis.candidateCount,
                        created: analysis.createdCount,
                      })}
                    </Text>
                    <Text size="sm" color="muted">
                      {t('livestockOverview.previous.expires', {
                        date: formatOverviewDate(analysis.expiresAt, i18n.language),
                      })}
                    </Text>
                  </Stack>
                  <Stack gap="2" align="end">
                    <Badge tone={STATUS_TONE[analysis.status]}>
                      {t(`livestockOverview.previous.status.${analysis.status}`)}
                    </Badge>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onOpen(analysis.id)}
                      aria-label={t('livestockOverview.previous.openLabel', { date })}
                    >
                      {t('livestockOverview.previous.open')}
                    </Button>
                  </Stack>
                </div>
              </Card>
            </li>
          );
        })}
      </ul>
    </Section>
  );
}

export default OverviewPreviousAnalyses;
